import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  AtSign, Plus, Search, Trash2, Edit3, Copy, Check, X, Mail, FolderPlus,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

export default function ErpEmails() {
  const { toast } = useToast();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const [newSection, setNewSection] = useState('');
  const [addingSection, setAddingSection] = useState(false);

  // per-section inline add-email inputs
  const [emailDraft, setEmailDraft] = useState({});
  // inline edit state
  const [editingEmail, setEditingEmail] = useState(null); // {sectionId, emailId}
  const [editValue, setEditValue] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [sectionNameValue, setSectionNameValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/email-directory');
      setSections(r.data.sections || []);
    } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return sections;
    const t = q.toLowerCase();
    return sections
      .map((s) => ({ ...s, emails: (s.emails || []).filter((e) => e.address.toLowerCase().includes(t) || s.name.toLowerCase().includes(t)) }))
      .filter((s) => s.name.toLowerCase().includes(t) || (s.emails || []).length > 0);
  }, [sections, q]);

  const copy = async (addr, eid) => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopiedId(eid);
      setTimeout(() => setCopiedId(null), 1400);
      toast({ title: 'Copied', description: addr });
    } catch { toast({ title: 'Copy failed', variant: 'destructive' }); }
  };

  const copyAll = async (s) => {
    const all = (s.emails || []).map((e) => e.address).join(', ');
    if (!all) return;
    try { await navigator.clipboard.writeText(all); toast({ title: `Copied all ${s.name} emails`, description: `${s.emails.length} address(es)` }); }
    catch { toast({ title: 'Copy failed', variant: 'destructive' }); }
  };

  const addSection = async () => {
    if (!newSection.trim()) return;
    try {
      await api.post('/email-directory', { name: newSection.trim() });
      setNewSection(''); setAddingSection(false); toast({ title: 'Section added' }); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const deleteSection = async (id) => {
    if (!window.confirm('Delete this whole section and its emails?')) return;
    try { await api.delete(`/email-directory/${id}`); toast({ title: 'Section deleted' }); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const saveSectionName = async (id) => {
    if (!sectionNameValue.trim()) return;
    try { await api.put(`/email-directory/${id}`, { name: sectionNameValue.trim() }); setEditingSection(null); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const addEmail = async (sectionId) => {
    const addr = (emailDraft[sectionId] || '').trim();
    if (!addr) return;
    try {
      await api.post(`/email-directory/${sectionId}/emails`, { address: addr });
      setEmailDraft((d) => ({ ...d, [sectionId]: '' }));
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const saveEmail = async () => {
    if (!editValue.trim()) return;
    try {
      await api.put(`/email-directory/${editingEmail.sectionId}/emails/${editingEmail.emailId}`, { address: editValue.trim() });
      setEditingEmail(null); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const deleteEmail = async (sectionId, emailId) => {
    try { await api.delete(`/email-directory/${sectionId}/emails/${emailId}`); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  return (
    <ErpShell title="Email Directory">
      <ErpPageHeader
        eyebrow="Shared address book"
        title="Email Directory"
        subtitle="Find and copy the right company emails in one tap — DHL, FedEx, UPS and more."
        icon={AtSign}
        actions={
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gg-text-dim)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or email…" className="gg-input ps-9" style={{ width: 240 }} data-testid="emails-search" />
            </div>
            <button onClick={() => setAddingSection(true)} className="gg-btn-primary" data-testid="add-section-btn"><FolderPlus size={14} /> Add Company</button>
          </>
        }
      />

      {addingSection && (
        <div className="gg-card p-4 mb-5 flex items-center gap-2" data-testid="add-section-row">
          <input value={newSection} onChange={(e) => setNewSection(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSection()} placeholder="Company / section name (e.g. Maersk)" className="gg-input flex-1" autoFocus data-testid="new-section-input" />
          <button onClick={addSection} className="gg-btn-primary" data-testid="save-section-btn"><Check size={14} /> Add</button>
          <button onClick={() => { setAddingSection(false); setNewSection(''); }} className="gg-btn-secondary">Cancel</button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="gg-card h-48 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <ErpEmpty icon={AtSign} title={q ? 'No matches' : 'No companies yet'} subtitle="Add a company and its email addresses so staff can copy them fast." action={<button onClick={() => setAddingSection(true)} className="gg-btn-primary"><FolderPlus size={14} /> Add Company</button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="emails-grid">
          {filtered.map((s, idx) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(idx * 0.04, 0.3) }} className="gg-card p-5" data-testid={`section-${s.id}`}>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}><Mail size={16} /></span>
                  {editingSection === s.id ? (
                    <span className="flex items-center gap-1">
                      <input value={sectionNameValue} onChange={(e) => setSectionNameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveSectionName(s.id)} className="gg-input" style={{ height: 34, width: 160 }} autoFocus data-testid={`section-name-input-${s.id}`} />
                      <button onClick={() => saveSectionName(s.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-emerald)' }}><Check size={14} /></button>
                      <button onClick={() => setEditingSection(null)} className="gg-btn-ghost"><X size={14} /></button>
                    </span>
                  ) : (
                    <h3 className="gg-section-title truncate" style={{ fontSize: 18 }} data-testid={`section-title-${s.id}`}>{s.name}</h3>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {(s.emails || []).length > 0 && <button onClick={() => copyAll(s)} className="gg-btn-ghost" title="Copy all" data-testid={`copy-all-${s.id}`}><Copy size={13} /> All</button>}
                  <button onClick={() => { setEditingSection(s.id); setSectionNameValue(s.name); }} className="gg-btn-ghost" title="Rename" data-testid={`rename-section-${s.id}`}><Edit3 size={13} /></button>
                  <button onClick={() => deleteSection(s.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} title="Delete section" data-testid={`delete-section-${s.id}`}><Trash2 size={13} /></button>
                </div>
              </div>

              <div className="space-y-2">
                {(s.emails || []).length === 0 && <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>No emails yet.</p>}
                {(s.emails || []).map((e) => (
                  <div key={e.id} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: 'var(--gg-surface-2)' }} data-testid={`email-${e.id}`}>
                    {editingEmail && editingEmail.emailId === e.id ? (
                      <>
                        <input value={editValue} onChange={(ev) => setEditValue(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && saveEmail()} className="gg-input flex-1" style={{ height: 32 }} autoFocus data-testid={`edit-email-input-${e.id}`} />
                        <button onClick={saveEmail} className="gg-btn-ghost" style={{ color: 'var(--gg-emerald)' }} data-testid={`save-email-${e.id}`}><Check size={14} /></button>
                        <button onClick={() => setEditingEmail(null)} className="gg-btn-ghost"><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => copy(e.address, e.id)} className="flex-1 text-start gg-tabular text-sm font-medium truncate hover:underline" style={{ color: 'var(--gg-text)' }} title="Click to copy" data-testid={`copy-email-${e.id}`}>
                          {e.address}
                        </button>
                        <button onClick={() => copy(e.address, e.id)} className="gg-btn-ghost" style={{ color: copiedId === e.id ? 'var(--gg-emerald)' : 'var(--gg-accent)' }} title="Copy" data-testid={`copy-btn-${e.id}`}>
                          {copiedId === e.id ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                        <button onClick={() => { setEditingEmail({ sectionId: s.id, emailId: e.id }); setEditValue(e.address); }} className="gg-btn-ghost" title="Edit" data-testid={`edit-email-${e.id}`}><Edit3 size={13} /></button>
                        <button onClick={() => deleteEmail(s.id, e.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} title="Delete" data-testid={`delete-email-${e.id}`}><Trash2 size={13} /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-3">
                <input
                  value={emailDraft[s.id] || ''}
                  onChange={(e) => setEmailDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && addEmail(s.id)}
                  placeholder="Add email address…"
                  className="gg-input flex-1"
                  style={{ height: 36 }}
                  data-testid={`add-email-input-${s.id}`}
                />
                <button onClick={() => addEmail(s.id)} className="gg-btn-secondary" style={{ height: 36 }} data-testid={`add-email-btn-${s.id}`}><Plus size={14} /> Add</button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </ErpShell>
  );
}
